import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Save, Globe, Eye, Copy, CheckCircle2, Loader2,
  User, Link2, ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicProfilePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/agents/me/public-profile`, { headers });
        setProfile(data);
      } catch { setError('Failed to load profile'); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const { data } = await axios.put(`${API}/api/agents/me/public-profile`, {
        slug: profile.slug,
        bio: profile.bio,
        headshot_url: profile.headshot_url,
        public_page_enabled: profile.public_page_enabled,
      }, { headers });
      setProfile(prev => ({ ...prev, ...data }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const pageUrl = profile?.slug ? `${window.location.origin}/agent/${profile.slug}` : '';

  const copyUrl = () => {
    navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="public-profile-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}
            className="text-muted-foreground hover:text-foreground" data-testid="back-btn">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-foreground">Public Agent Page</h1>
            <p className="text-xs text-muted-foreground">Manage your public-facing profile</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving}
          className="bg-amber-600 hover:bg-amber-500 text-foreground gap-2"
          data-testid="save-profile-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <CheckCircle2 size={14} /> : <Save size={14} />}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </Button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400" data-testid="profile-error">
          {error}
        </div>
      )}

      {/* Page Visibility & URL */}
      <Card className="border-border bg-card" data-testid="visibility-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Globe size={16} className="text-amber-400" /> Page Visibility
          </CardTitle>
          <CardDescription className="text-muted-foreground text-xs">
            Control whether your public agent page is visible to potential clients
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <Eye size={16} className="text-muted-foreground" />
              <div>
                <p className="text-sm text-foreground font-medium">Public Page Enabled</p>
                <p className="text-xs text-muted-foreground">When enabled, your page is accessible to anyone with the link</p>
              </div>
            </div>
            <Switch checked={profile?.public_page_enabled ?? false}
              onCheckedChange={v => setProfile(p => ({ ...p, public_page_enabled: v }))}
              data-testid="toggle-public-page" />
          </div>

          {profile?.slug && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <Link2 size={14} className="text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground truncate flex-1 font-mono">{pageUrl}</span>
              <Button size="sm" variant="ghost" onClick={copyUrl}
                className="text-muted-foreground hover:text-foreground shrink-0 h-7 text-xs gap-1"
                data-testid="copy-url-btn">
                {copied ? <CheckCircle2 size={12} className="text-emerald-400" /> : <Copy size={12} />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <a href={`/agent/${profile.slug}`} target="_blank" rel="noreferrer">
                <Button size="sm" variant="ghost"
                  className="text-muted-foreground hover:text-foreground shrink-0 h-7 text-xs gap-1"
                  data-testid="preview-page-btn">
                  <ExternalLink size={12} /> Preview
                </Button>
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profile Details */}
      <Card className="border-border bg-card" data-testid="profile-details-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <User size={16} className="text-blue-400" /> Profile Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Slug */}
          <div>
            <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Page URL Slug</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground shrink-0">/agent/</span>
              <Input value={profile?.slug || ''}
                onChange={e => setProfile(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                placeholder="your-name"
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground font-mono text-sm"
                data-testid="slug-input" />
            </div>
          </div>

          {/* Headshot URL */}
          <div>
            <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Headshot Photo URL</label>
            <Input value={profile?.headshot_url || ''}
              onChange={e => setProfile(p => ({ ...p, headshot_url: e.target.value }))}
              placeholder="https://example.com/photo.jpg"
              className="bg-muted border-border text-foreground placeholder:text-muted-foreground text-sm"
              data-testid="headshot-input" />
            {profile?.headshot_url && (
              <div className="mt-2">
                <img src={profile.headshot_url} alt="Headshot preview"
                  className="w-16 h-16 rounded-lg object-cover border border-border"
                  onError={e => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>

          {/* Bio */}
          <div>
            <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Bio / About</label>
            <Textarea value={profile?.bio || ''}
              onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
              placeholder="Tell potential clients about yourself, your experience, and what makes you unique..."
              rows={4}
              className="bg-muted border-border text-foreground placeholder:text-muted-foreground text-sm"
              data-testid="bio-input" />
            <p className="text-[10px] text-muted-foreground mt-1">{(profile?.bio || '').length}/500 characters</p>
          </div>
        </CardContent>
      </Card>

      {/* Preview Info */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
              <Globe size={16} className="text-amber-400" />
            </div>
            <div>
              <p className="text-sm text-foreground font-medium">Your Public Page Includes</p>
              <ul className="mt-2 space-y-1">
                {['Your name, brokerage & contact info', 'Bio & specialties', 'Featured listings from your market', 'Lead capture form for potential clients'].map((item, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <CheckCircle2 size={10} className="text-emerald-400" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
